import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/api";
import StatsChart from "../components/StatsChart";
import { Card, Button, Table, THead, TBody, TRow, TH, TD, PageHeader, KpiCard, PageSection, Toolbar, PageLayout, FadeIn } from "@dawa/ui";
import { FileText, Briefcase, Search, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";

interface Stats {
  totalContents: number;
  activeProjects: number;
  trackedKeywords: number;
  avgPosition: number;
}

export default function Dashboard() {
  const [stats, setStats] = useState<Stats>({
    totalContents: 0,
    activeProjects: 0,
    trackedKeywords: 0,
    avgPosition: 0,
  });
  const [recent, setRecent] = useState<any[]>([]);
  const [rowsMounted, setRowsMounted] = useState(false);
  const [q, setQ] = useState("");
  const [name, setName] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const s = await supabase.auth.getSession();
      const token = s.data.session?.access_token;
      const user = await supabase.auth.getUser();
      setName(user.data.user?.user_metadata?.name || user.data.user?.email || "");

      const r = await apiFetch("/v1/contents?stats=1", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      setStats({
        totalContents: j.stats?.total ?? 0,
        activeProjects: j.stats?.active ?? 0,
        trackedKeywords: j.stats?.keywords ?? 0,
        avgPosition: j.stats?.avg ?? 0,
      });

      const res = await apiFetch("/v1/contents", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = await res.json();
      setRecent(list.slice(0, 5));
      setTimeout(() => setRowsMounted(true), 0);
    })();
  }, []);

  const kpis = [
    {
      label: "Contenus générés",
      value: stats.totalContents,
      icon: FileText,
    },
    {
      label: "Projets actifs",
      value: stats.activeProjects,
      icon: Briefcase,
    },
    {
      label: "Mots-clés suivis",
      value: stats.trackedKeywords,
      icon: Search,
    },
    {
      label: "Position moyenne",
      value: stats.avgPosition,
      icon: TrendingUp,
    },
  ];

  const actions = [
    {
      label: "Nouveau contenu",
      icon: FileText,
      href: "/content/new",
    },
    {
      label: "Recherche de mots-clés",
      icon: Search,
      href: "/keywords",
    },
    {
      label: "Mes projets",
      icon: Briefcase,
      href: "/projects",
    },
  ];

  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 },
  };

  const filtered = q ? recent.filter((c) => String(c.primary_keyword || "").toLowerCase().includes(q.toLowerCase())) : recent;

  return (
    <PageLayout maxWidth="full" className="space-y-12">
      <motion.section {...fadeUp}>
        <PageHeader
          title={<>Bonjour {name} 👋</>}
          subtitle="Prêt à booster votre SEO aujourd'hui ?"
          breadcrumbs={[{ label: "Accueil", href: "/dashboard" }, { label: "Tableau de bord" }]}
          actions={<Button onClick={() => navigate("/content/new")}>Nouveau contenu</Button>}
        />
      </motion.section>

      <motion.section {...fadeUp} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {actions.map((a) => (
          <Card key={a.label} className="flex flex-col items-center gap-4 p-6 text-center">
            <a.icon className="h-8 w-8 text-accent" />
            <Button onClick={() => navigate(a.href)}>{a.label}</Button>
          </Card>
        ))}
      </motion.section>

      <motion.section {...fadeUp} className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k, i) => (
          <FadeIn key={k.label} delayMs={i * 60}>
            <KpiCard label={k.label} value={k.value} icon={<k.icon className="h-8 w-8" />} />
          </FadeIn>
        ))}
      </motion.section>

      <motion.section {...fadeUp}>
        <PageSection title="Évolution des positions SEO">
          <Card variant="elevated" className="p-4">
            <StatsChart />
          </Card>
        </PageSection>
      </motion.section>

      <motion.section {...fadeUp}>
        <PageSection title="Derniers contenus générés">
          <Toolbar searchValue={q} onSearchChange={setQ} placeholder="Filtrer par mot-clé" />
          <FadeIn>
          <div className="overflow-x-auto">
            <Table>
              <THead>
                <TRow>
                  <TH>Mot-clé</TH>
                  <TH>Date</TH>
                </TRow>
              </THead>
              <TBody>
              {filtered.map((c, i) => (
                <TRow
                  key={c.id}
                  className={`transition-all duration-300 ${rowsMounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'}`}
                  style={{ transitionDelay: `${i * 40}ms` }}
                >
                  <TD className="font-medium">{c.primary_keyword}</TD>
                  <TD className="text-muted">{new Date(c.created_at).toLocaleDateString()}</TD>
                </TRow>
              ))}
              </TBody>
            </Table>
          </div>
          </FadeIn>
        </PageSection>
      </motion.section>

      <motion.section {...fadeUp} className="text-center">
        <Button onClick={() => navigate("/content/new")}>Créer un nouveau contenu</Button>
      </motion.section>
    </PageLayout>
  );
}
